import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  CheckCircle,
  AlertTriangle,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Receipt,
  CreditCard,
  Banknote,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';

// Billing Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { color: 'warning', text: 'Draft', bgColor: '#fef3c7', textColor: '#92400e' },
    pending: { color: 'info', text: 'Pending', bgColor: '#dbeafe', textColor: '#1e40af' },
    sent: { color: 'primary', text: 'Sent', bgColor: '#e0e7ff', textColor: '#3730a3' },
    paid: { color: 'success', text: 'Paid', bgColor: '#d1fae5', textColor: '#065f46' },
    overdue: { color: 'danger', text: 'Overdue', bgColor: '#fee2e2', textColor: '#991b1b' },
    cancelled: { color: 'secondary', text: 'Cancelled', bgColor: '#f3f4f6', textColor: '#374151' }
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <span 
      className="badge" 
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: 'var(--font-family)'
      }}
    >
      {config.text}
    </span>
  );
};

// Action Dropdown Component
const ActionDropdown = ({
  billing,
  onView,
  onEdit,
  onDelete,
  onSend,
  onMarkPaid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 180);
    }
    setIsOpen((v) => !v);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[190px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onView(billing);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Invoice
              </button>
            </li>

            <li>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onEdit(billing);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Invoice
              </button>
            </li>

            {billing.status === 'draft' && (
              <li>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onSend(billing);
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 text-left"
                >
                  <Receipt size={16} className="mr-2.5 text-emerald-600" />
                  Send Invoice
                </button>
              </li>
            )}

            {billing.status === 'sent' && (
              <li>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onMarkPaid(billing);
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 text-left"
                >
                  <CheckCircle size={16} className="mr-2.5 text-emerald-600" />
                  Mark as Paid
                </button>
              </li>
            )}

            <li className="border-t border-gray-200">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onDelete(billing);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Invoice
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};


const StatCard = ({ title, value, icon: Icon, iconBg, iconColor, suffix = '' }) => {
  return (
    <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
      <div className="h-1 w-full bg-[var(--primary-color)]" />
      <div className="relative flex items-center gap-3 p-4">
        <div
          className="flex h-11 w-11 items-center justify-center"
          style={{ backgroundColor: iconBg }}
        >
          <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div>
          <div className="text-[20px] font-bold">
            {value}{suffix}
          </div>
          <div className="text-[13px] text-gray-500">{title}</div>
        </div>
      </div>
    </div>
  );
};

const Billing = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // State management
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [billingToDelete, setBillingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingPayment: 0,
    paidThisMonth: 0,
    totalRevenue: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const loadBillings = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString()
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}?${queryParams}`, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to load invoices');
      }

      const data = await response.json();
      const invoices = data.data || [];

      const mappedBillings = invoices.map((invoice) => ({
        id: invoice._id || invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        client: invoice.clientName || '',
        project: invoice.projectName || '',
        date: invoice.date,
        dueDate: invoice.dueDate,
        amount: invoice.subtotal || 0,
        status: invoice.status || 'draft',
        paidDate: invoice.paidDate,
        paymentMethod: invoice.paymentMethod || null,
        description: invoice.description || '',
        hours: invoice.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0,
        hourlyRate: invoice.items?.[0]?.rate || 0,
        tax: invoice.taxAmount || 0,
        total: invoice.total || 0
      }));

      setBillings(mappedBillings);
      setStats({
        totalInvoices: data.pagination?.totalItems || mappedBillings.length,
        pendingPayment: mappedBillings.filter(b => b.status === 'sent' || b.status === 'overdue').length,
        paidThisMonth: mappedBillings.filter(b => b.status === 'paid').length,
        totalRevenue: mappedBillings.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0)
      });

      setPagination(prev => ({
        ...prev,
        totalItems: data.pagination?.totalItems || mappedBillings.length,
        totalPages: data.pagination?.totalPages || 1,
        currentPage: data.pagination?.currentPage || prev.currentPage
      }));
    } catch (error) {
      console.error('Error loading billings:', error);
      showError(error.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const filteredBillings = billings.filter((b) => {
    const query = search.toLowerCase();
    
    return (
      b.invoiceNumber?.toLowerCase().includes(query) ||
      b.client?.toLowerCase().includes(query) ||
      b.project?.toLowerCase().includes(query)
    );
  });

  const handleRefresh = () => {
    loadBillings();
  };

  useEffect(() => {
    loadBillings();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  const handleCreateInvoice = () => {
    navigate('/projects/time-tracking/billing/create');
  };

  const handleViewBilling = (billing) => {
    navigate(`/projects/time-tracking/billing/view/${billing.id}`);
  };

  const handleEditBilling = (billing) => {
    navigate(`/projects/time-tracking/billing/edit/${billing.id}`);
  };

  const handleDeleteBilling = (billing) => {
    setBillingToDelete(billing);
    setShowDeleteModal(true);
  };

  const handleSendInvoice = async (billing) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${billing.id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify({ status: 'sent' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send invoice');
      }

      success('Invoice sent successfully');
      loadBillings();
    } catch (error) {
      console.error('Error sending invoice:', error);
      showError(error.message || 'Failed to send invoice');
    }
  };

  const handleMarkPaid = async (billing) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${billing.id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify({
          status: 'paid',
          paidDate: new Date().toISOString(),
          paymentMethod: 'Bank Transfer'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to mark invoice as paid');
      }

      success('Invoice marked as paid');
      loadBillings();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      showError(error.message || 'Failed to mark invoice as paid');
    }
  };

  const confirmDelete = async () => {
    if (!billingToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${billingToDelete.id}`, {
        method: 'DELETE',
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete invoice');
      }

      success('Invoice deleted successfully');
      loadBillings();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showError(error.message || 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setBillingToDelete(null);
    }
  };
  
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      disabled: loading,
    },
    {
      label: 'New Invoice',
      icon: <Plus size={18} />,
      onClick: handleCreateInvoice,
      variant: 'primary',
    },
  ];

  // Table columns
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      render: (value, billing) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {billing.invoiceNumber}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {new Date(billing.date).toLocaleDateString()}
          </div>
        </div>
      )
    },
    {
      key: 'client',
      label: 'Client',
      render: (value, billing) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {billing.client}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {billing.project}
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value, billing) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            ${billing.amount.toLocaleString()}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {billing.hours}h @ ${billing.hourlyRate}/hr
          </div>
        </div>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (value, billing) => (
        <div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {new Date(billing.dueDate).toLocaleDateString()}
          </div>
          {billing.status === 'overdue' && (
            <div style={{ color: '#dc2626', fontSize: '12px', fontWeight: '600' }}>
              OVERDUE
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, billing) => <StatusBadge status={billing.status} />
    },
    {
      key: 'paidDate',
      label: 'Payment',
      render: (value, billing) => (
        <div>
          {billing.paidDate ? (
            <>
              <div style={{ fontSize: '14px', color: '#059669', fontWeight: '600' }}>
                Paid {new Date(billing.paidDate).toLocaleDateString()}
              </div>
              <div style={{ color: '#6b7280', fontSize: '12px' }}>
                {billing.paymentMethod}
              </div>
            </>
          ) : (
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Not paid
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, billing) => (
        <ActionDropdown 
          billing={billing}
          onView={handleViewBilling}
          onEdit={handleEditBilling}
          onDelete={handleDeleteBilling}
          onSend={handleSendInvoice}
          onMarkPaid={handleMarkPaid}
        />
      )
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Billing"
        description="Manage invoices, payments, and billing for time tracking"
        actions={headerActions}
      />
      <div className="page-container">

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <StatCard
         title="Total Invoices" value={stats.totalInvoices} icon={Receipt} iconBg="#E0F2FE" iconColor="#0284C7"
        />
        
        <StatCard
          title="Pending Payment"
          value={stats.pendingPayment}
          icon={AlertTriangle}
          iconBg="#FEF3C7"
          iconColor="#D97706"
        />

        <StatCard
          title="Paid This Month"
          value={stats.paidThisMonth}
          icon={CheckCircle}
          iconBg="#DCFCE7"
          iconColor="#16A34A"
        />

        <StatCard
          title="Total Revenue"
          value={stats.totalRevenue.toLocaleString()}
          suffix="$"
          icon={DollarSign}
          iconBg="#EDE9FE"
          iconColor="#7C3AED"
        />
      </div>

      {/* Billing Table */}
      <DataTable
      data={filteredBillings}
      columns={columns}
      title="Invoices"
      loading={loading}
      pagination={pagination}
      onPaginationChange={setPagination}
      emptyMessage="No invoices found. Create your first invoice to get started."
      emptyIcon={<Receipt size={48} />}
      showActions={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={confirmDelete}
      title="Delete Invoice"
      message={`Are you sure you want to delete invoice ${
        billingToDelete?.invoiceNumber || ''
      }? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
      loading={isDeleting}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default Billing;
